import { type BuilderContent, Content } from "@builder.io/sdk-react/edge";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";

import {
  CUSTOM_COMPONENTS,
  configureBuilder,
} from "~/components/builder-registry";
import { useConsent } from "~/contexts/consent";
import { createBrandMeta } from "~/lib/shared/meta";
import { clientEnv } from "~/lib/utils/client-env";

const BUILDER_MODEL = "page";
const SUMMIT_URL_PATH = "/page/summit";

const BuilderPageDataSchema = z
  .object({
    title: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    excerpt: z.string().optional(),
    image: z.string().optional(),
    heroImage: z.string().optional(),
    keywords: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
  })
  .passthrough();

export const Route = createFileRoute("/summit/")({
  staleTime: 6 * 60 * 60 * 1000, // 6 hours
  loader: async ({ location }) => {
    const [{ cachedFetchOneEntry }, { getBuilderSearchParams }] =
      await Promise.all([
        import("~/lib/builder/cache"),
        import("@builder.io/sdk-react/edge"),
      ]);
    const builderContent = await cachedFetchOneEntry({
      model: BUILDER_MODEL,
      apiKey: clientEnv.VITE_BUILDER_API_KEY,
      userAttributes: { urlPath: SUMMIT_URL_PATH },
      includeUnpublished: false,
      options: getBuilderSearchParams(new URLSearchParams(location.search)),
    });

    if (!builderContent) {
      throw notFound();
    }

    return { content: builderContent };
  },
  head: ({ loaderData }) => {
    const content = loaderData?.content ?? null;
    const parsedPageData = BuilderPageDataSchema.safeParse(content?.data);
    const pageData = parsedPageData.success ? parsedPageData.data : null;

    if (!pageData) {
      return createBrandMeta({
        title: "Dream Coach Match Summit",
        description:
          "Register for the Dream Coach Match Summit to learn from top coaches and grow your practice.",
        ogType: "website",
        canonical: "https://dreamcoachmatch.com/summit",
        ogUrl: "https://dreamcoachmatch.com/summit",
      });
    }

    const title = pageData.title || pageData.name || "Dream Coach Match Summit";
    const description =
      pageData.description ||
      pageData.excerpt ||
      "Register for the Dream Coach Match Summit to learn from top coaches and grow your practice.";
    const image = pageData.image || pageData.heroImage;
    const url = "https://dreamcoachmatch.com/summit";

    return createBrandMeta({
      title,
      description,
      ogImage: image,
      ogUrl: url,
      canonical: url,
      keywords: pageData.keywords || pageData.tags,
      ogType: "website",
    });
  },
  component: SummitLandingPage,
});

function SummitLandingPage() {
  const { content } = Route.useLoaderData() as { content: BuilderContent };
  const { allowed } = useConsent();

  useEffect(() => {
    configureBuilder();
  }, []);

  return (
    <Content
      model={BUILDER_MODEL}
      content={content}
      apiKey={clientEnv.VITE_BUILDER_API_KEY}
      customComponents={CUSTOM_COMPONENTS}
      canTrack={allowed.analytics}
    />
  );
}
